import { FOVMarker, MapBase } from '@shared/map';
import * as turf from '@turf/turf';
import { updateAnnotationsAsync } from 'actions/annotation-actions';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CombinedState } from 'reducers';
import { useDeduplicationTask } from './hooks';
import { DeduplicationPair } from './model';
import { RefToMap, useDeduplicationMap } from './hooks/use-deduplication-map';
import { DeduplicationTask } from './deduplication-task';
import { DEDUPLICATION_MAP_ID, MAGENTA_FILL, MAGENTA_STROKE, ORANGE_FILL, YELLOW_FILL, YELLOW_STROKE } from './consts';

const computeBounds = (
    cameraLeft: DeduplicationPair['camera_left'] | null | undefined,
    cameraRight: DeduplicationPair['camera_right'] | null | undefined,
): [number, number, number, number] | null => {
    const features: turf.Feature[] = [];

    if (cameraLeft?.location) {
        features.push(turf.point([cameraLeft.location.lon, cameraLeft.location.lat]));
    }
    if (cameraRight?.location) {
        features.push(turf.point([cameraRight.location.lon, cameraRight.location.lat]));
    }

    if (features.length === 0) return null;

    return turf.bbox(turf.featureCollection(features)) as [number, number, number, number];
};

const useCenterFrame = (task: DeduplicationTask, map: RefToMap) => {
    const { pairData, bounds, frame } = task;
    if (!pairData) return;
    const cameraLeft = pairData.camera_left;
    const cameraRight = pairData.camera_right;
    const [centeredFrame, setCenteredFrame] = useState(NaN);
    useEffect(() => {
        if (map.current && cameraLeft?.location && cameraRight?.location && frame !== centeredFrame) {
            console.log('centering frame', frame);
            const bearing = cameraLeft?.fov?.bearing === undefined ? 0 : 90 - cameraLeft?.fov?.bearing;
            map.current.fitBounds(bounds, {
                animate: false,
                bearing,
                pitch: 0,
                maxZoom: 20,
                padding: 80,
            });
            setCenteredFrame(frame);
        }
        else{
            // log which condition(s) are not being met
            console.log('condition not met', {
                cameraLeftLocation: cameraLeft?.location,
                cameraRightLocation: cameraRight?.location,
                frame: frame,
                centeredFrame: centeredFrame,
                frameNotEqualCenteredFrame: frame !== centeredFrame,
                mapExists: !!map.current,
                boundsExists: !!bounds,
            });
        }
    }, [bounds, frame, centeredFrame, map]);
};

// Component that uses the map hook - must be rendered inside MapBase's MapProvider
const MapContent: React.FC<{
    task: DeduplicationTask;
    cameraLeft: DeduplicationPair['camera_left'] | null | undefined;
    cameraRight: DeduplicationPair['camera_right'] | null | undefined;
}> = ({ task, cameraLeft, cameraRight }) => {
    const map = useDeduplicationMap();
    useCenterFrame(task, map);

    return (
        <>
            {cameraLeft?.location && cameraLeft?.fov ? (
                <FOVMarker
                    lat={cameraLeft.location.lat}
                    lon={cameraLeft.location.lon}
                    bearing={cameraLeft.fov.bearing}
                    fov={cameraLeft.fov.fov}
                    near={cameraLeft.fov.near}
                    far={cameraLeft.fov.far}
                    fillColor={YELLOW_FILL}
                    strokeColor={YELLOW_STROKE}
                    strokeWidth={0.3}
                />
            ) : null}
            {cameraRight?.location && cameraRight?.fov ? (
                <FOVMarker
                    lat={cameraRight.location.lat}
                    lon={cameraRight.location.lon}
                    bearing={cameraRight.fov.bearing}
                    fov={cameraRight.fov.fov}
                    near={cameraRight.fov.near}
                    far={cameraRight.fov.far}
                    fillColor={MAGENTA_FILL}
                    strokeColor={MAGENTA_STROKE}
                    strokeWidth={0.3}
                />
            ) : null}
        </>
    );
};

// private computeBounds() {
//     const camera = this.camera?.location ? turf.point([this.camera.location.lon, this.camera.location.lat]) : null;
//     const assets = this.assets.map((a) => a.location && turf.point([a.location.lon, a.location.lat]));
//     let fov: turf.Feature[] = [];
//     if (camera && this.camera?.fov) {
//         const { far, bearing: ccwEBearing, fov: angle } = this.camera.fov;

//         const bearing = convertBearing(ccwEBearing);
//         const center = turf.destination(camera, (far / 2) * 1e-3, bearing);
//         const sin = Math.sin(angle / 2);
//         const diag = far * Math.sqrt(0.25 + sin * sin) * 1e-3;
//         fov = [
//             turf.destination(center, diag, normalizeBearing(bearing + 45)),
//             turf.destination(center, diag, normalizeBearing(bearing + 135)),
//             turf.destination(center, diag, normalizeBearing(bearing + 225)),
//             turf.destination(center, diag, normalizeBearing(bearing + 315)),
//         ];
//     }

//     const features = [camera, ...assets, ...fov].filter((f): f is turf.Feature => !!f);

//     const bounds = turf.bbox(
//         turf.featureCollection(
//             features.length > 0 ? features : [turf.point([DEFAULT_MAP_LOCATION.longitude, DEFAULT_MAP_LOCATION.latitude])],
//         ),
//     );
//     return bounds as [number, number, number, number];
// }

export const DeduplicationView: React.FC = () => {
    const task = useDeduplicationTask();
    const currentFrame = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    const pair = task.getCurrentPair(currentFrame);

    if (!pair?.values.pair_data) {
        return (
            <div style={{ padding: '16px' }}>
                <p>No deduplication pair tag found for frame {currentFrame}</p>
            </div>
        );
    }

    const { camera_left, camera_right } = pair.values.pair_data;

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0 }}>
                <MapBase mapId={DEDUPLICATION_MAP_ID} initialViewState={task.defaultViewState}>
                    <MapContent task={task} cameraLeft={camera_left} cameraRight={camera_right} />
                </MapBase>
            </div>
            {/* <StatusButtons currentValue={is_duplicate} onChange={updateIsDuplicate} /> */}
        </div>
    );
};
